import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import SettingsTabulator from '../../../components/layouts/SettingsTabulator';
import {
    getGoogleMerchantConfiguration,
    updateGoogleMerchantConfiguration,
    testGoogleMerchantConnection,
    createGoogleMerchantProfilesForActiveLanguages,
    createGoogleMerchantDataSourceForProfile,
    getGoogleMerchantOperationalSettings,
    getGoogleMerchantAccounts,
    prepareGoogleMerchantProduct,
    markGoogleMerchantProductsDirty,
    searchGoogleMerchantExportStates,
    prepareGoogleMerchantPending,
    sendGoogleMerchantPrepared,
    deleteGoogleMerchantProfile,
    createGoogleMerchantProfile,
    updateGoogleMerchantProfile,
    searchGoogleMerchantWorkerHistory,
    markGoogleMerchantVisibleProductsDirty,
} from '../../../redux/methods/integrations';

import SettingsSection from '../../../features/integrations/googleMerchant/editTabs/SettingsSection';
import SynchronizationSection from '../../../features/integrations/googleMerchant/editTabs/SynchronizationSection';
import DiagnosticsSection from '../../../features/integrations/googleMerchant/editTabs/DiagnosticsSection';
import ProfilesSection from '../../../features/integrations/googleMerchant/editTabs/ProfilesSection';

type GoogleMerchantProperties =
{
    getGoogleMerchantConfiguration: () => Promise<any>
    updateGoogleMerchantConfiguration: (values: any) => Promise<any>
    testGoogleMerchantConnection: (values: any) => Promise<any>
    createGoogleMerchantProfilesForActiveLanguages: () => Promise<any>
    createGoogleMerchantDataSourceForProfile: (profileId: string) => Promise<any>
    getGoogleMerchantOperationalSettings: () => Promise<any>
    getGoogleMerchantAccounts: () => Promise<any>
    prepareGoogleMerchantProduct: (values: any) => Promise<any>
    markGoogleMerchantProductsDirty: (values: any) => Promise<any>
    searchGoogleMerchantExportStates: (values: any) => Promise<any>
    searchGoogleMerchantWorkerHistory: (values: any) => Promise<any>
    prepareGoogleMerchantPending: (values: any) => Promise<any>
    sendGoogleMerchantPrepared: (values: any) => Promise<any>
    createGoogleMerchantProfile: (profile: any) => Promise<any>
    updateGoogleMerchantProfile: (profileId: string, profile: any) => Promise<any>
    deleteGoogleMerchantProfile: (profileId: string) => Promise<any>
    markGoogleMerchantVisibleProductsDirty: (values: any) => Promise<any>
}

const unwrapPayload = (result: any) => result?.payload ?? result;

function GoogleMerchant(props: GoogleMerchantProperties)
{
    const [configuration, setConfiguration] = useState<any | null>(null);
    const [testResult, setTestResult] = useState<any | null>(null);
    const [lastError, setLastError] = useState<string | null>(null);

    const connection = configuration?.connection ?? null;
    const profiles = configuration?.profiles?.exportProfiles ?? [];

    const loadSettings = async () =>
    {
        const result = await props.getGoogleMerchantConfiguration();
        setConfiguration(unwrapPayload(result));
    };

    useEffect(() =>
    {
        loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSaveSettings = async (values: any) =>
    {
        setLastError(null);

        try
        {
            const result = await props.updateGoogleMerchantConfiguration(values);
            const payload = unwrapPayload(result);

            if (payload)
            {
                setConfiguration(payload);
            }
            else
            {
                setLastError('Zapis konfiguracji nie zwrócił odpowiedzi. Sprawdź komunikat błędu globalnego albo log backendu.');
            }
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Zapis konfiguracji zakończył się błędem.');
            throw error;
        }
    };

    const handleTestConnection = async (values: any) =>
    {
        setLastError(null);
        setTestResult(null);

        try
        {
            const result = await props.testGoogleMerchantConnection(values);
            const payload = unwrapPayload(result);

            if (payload)
            {
                setTestResult(payload);
                await loadSettings();
            }
            else
            {
                setLastError('Test połączenia nie zwrócił odpowiedzi. Sprawdź komunikat błędu globalnego albo log backendu.');
            }

            return payload;
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Test połączenia zakończył się błędem.');
            throw error;
        }
    };

    const handleCreateProfilesForActiveLanguages = async () =>
    {
        setLastError(null);

        try
        {
            const result = await props.createGoogleMerchantProfilesForActiveLanguages();
            const payload = unwrapPayload(result);

            if (payload)
            {
                setConfiguration(payload);
            }
            else
            {
                setLastError('Nie udało się utworzyć profili na podstawie aktywnych języków.');
            }
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Tworzenie profili na podstawie aktywnych języków zakończyło się błędem.');
            throw error;
        }
    };

    const handleCreateDataSourceForProfile = async (profileId: string) =>
    {
        setLastError(null);

        try
        {
            const result = await props.createGoogleMerchantDataSourceForProfile(profileId);
            const payload = unwrapPayload(result);

            if (payload?.configuration)
            {
                setConfiguration(payload.configuration);
            }

            if (!payload)
            {
                throw new Error('Tworzenie źródła danych nie zwróciło odpowiedzi. Sprawdź komunikat błędu globalnego albo log backendu.');
            }

            return payload;
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Tworzenie źródła danych zakończyło się błędem.');
            throw error;
        }
    };

    const handleCreateProfile = async (profile: any) =>
    {
        setLastError(null);

        try
        {
            const result = await props.createGoogleMerchantProfile(profile);
            const payload = unwrapPayload(result);

            if (payload)
            {
                setConfiguration(payload);
            }

            return payload;
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Tworzenie profilu zakończyło się błędem.');
            throw error;
        }
    };

    const handleUpdateProfile = async (profileId: string, profile: any) =>
    {
        setLastError(null);

        try
        {
            const result = await props.updateGoogleMerchantProfile(profileId, profile);
            const payload = unwrapPayload(result);

            if (payload)
            {
                setConfiguration(payload);
            }

            return payload;
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Zapis profilu zakończył się błędem.');
            throw error;
        }
    };

    const handleDeleteProfile = async (profileId: string) =>
    {
        setLastError(null);

        try
        {
            const result = await props.deleteGoogleMerchantProfile(profileId);
            const payload = unwrapPayload(result);

            if (payload)
            {
                setConfiguration(payload);
            }
            else
            {
                setLastError('Usunięcie profilu nie zwróciło odpowiedzi.');
            }

            return payload;
        }
        catch (error: any)
        {
            setLastError(error?.message ?? 'Usunięcie profilu zakończyło się błędem.');
            throw error;
        }
    };

    const providerCredentials = configuration?.providerCredentials ?? null;

    const refreshTabComponent = (tabulatorId: string) =>
    {
        switch (tabulatorId)
        {
            case 'GoogleMerchant-settings':
                return <SettingsSection
                    settings={connection}
                    testResult={testResult}
                    lastError={lastError}
                    onSubmit={handleSaveSettings}
                    onTestConnection={handleTestConnection}
                    providerCredentials={providerCredentials}
                />;

            case 'GoogleMerchant-profiles':
                return <ProfilesSection
                    connection={connection}
                    profiles={profiles}
                    onCreateProfilesForActiveLanguages={handleCreateProfilesForActiveLanguages}
                    onCreateDataSourceForProfile={handleCreateDataSourceForProfile}
                    onCreateProfile={handleCreateProfile}
                    onUpdateProfile={handleUpdateProfile}
                    onDeleteProfile={handleDeleteProfile}
                />;

            case 'GoogleMerchant-synchronization':
                return <SynchronizationSection
                    settings={{ ...(connection ?? {}), exportProfiles: profiles }}
                    markVisibleProductsDirty={props.markGoogleMerchantVisibleProductsDirty}
                    searchWorkerHistory={props.searchGoogleMerchantWorkerHistory}
                />;

            case 'GoogleMerchant-diagnostics':
                return <DiagnosticsSection
                    settings={{ ...(connection ?? {}), exportProfiles: profiles }}
                    getGoogleMerchantAccounts={props.getGoogleMerchantAccounts}
                    prepareGoogleMerchantProduct={props.prepareGoogleMerchantProduct}
                    markProductsDirty={props.markGoogleMerchantProductsDirty}
                    searchExportStates={props.searchGoogleMerchantExportStates}
                    preparePending={props.prepareGoogleMerchantPending}
                    sendPrepared={props.sendGoogleMerchantPrepared}
                />;

            default:
                return null;
        }
    };

    return (
        <SettingsTabulator
            tabulatorHeader={{
                label: 'Google Merchant Center',
                iconClassName: 'fab fa-google'
            }}
            refreshTabComponent={refreshTabComponent}
            customActiveTabId="GoogleMerchant-settings"
            tabs={[
                {
                    id: 'GoogleMerchant-settings',
                    header: {
                        label: 'Połączenie',
                        iconClassName: 'fa fa-plug'
                    }
                },
                {
                    id: 'GoogleMerchant-profiles',
                    header: {
                        label: 'Profile eksportu',
                        iconClassName: 'fa fa-list'
                    }
                },
                {
                    id: 'GoogleMerchant-synchronization',
                    header: {
                        label: 'Eksport produktów',
                        iconClassName: 'fa fa-sync'
                    }
                },
                {
                    id: 'GoogleMerchant-diagnostics',
                    header: {
                        label: 'Diagnostyka',
                        iconClassName: 'fa fa-stethoscope'
                    }
                }
            ]}
        />
    );
}

const mapDispatch =
{
    getGoogleMerchantConfiguration,
    updateGoogleMerchantConfiguration,
    testGoogleMerchantConnection,
    createGoogleMerchantProfilesForActiveLanguages,
    createGoogleMerchantDataSourceForProfile,
    getGoogleMerchantOperationalSettings,
    getGoogleMerchantAccounts,
    prepareGoogleMerchantProduct,
    markGoogleMerchantProductsDirty,
    searchGoogleMerchantExportStates,
    searchGoogleMerchantWorkerHistory,
    prepareGoogleMerchantPending,
    sendGoogleMerchantPrepared,
    createGoogleMerchantProfile,
    updateGoogleMerchantProfile,
    deleteGoogleMerchantProfile,
    markGoogleMerchantVisibleProductsDirty
};

export default connect(null, mapDispatch)(GoogleMerchant);
